import { Experiment } from "@amplitude/experiment-js-client";
import type { ExperimentClient, Variant, Variants } from "@amplitude/experiment-js-client";
import type { FeatureFlagKeys } from "types/feature-flags";

const deploymentKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

/**
 * FeatureFlagService is a singleton class that evaluates feature flags
 * and experiment variants using Amplitude Experiment.
 *
 * @important if we want to account for AdBlock users, we should consider using
 * a server-side solution for feature flags instead of relying on the client-side Amplitude Experiment SDK.
 * Similar to PostHog implementation in the past, we should go through `users/me/feature-flags` endpoint to get the feature flags for the user.
 */
class FeatureFlagService {
  private static client: ExperimentClient | null = null;
  private static isInitialized = false;

  /**
   * Initialize the Amplitude Experiment client and fetch variants for the user.
   */
  static async init() {
    if (FeatureFlagService.isInitialized) {
      console.warn("FeatureFlagService already initialized");
      return;
    }

    if (!deploymentKey) {
      console.warn("REACT_APP_AMPLITUDE_API_KEY not found");
      return;
    }

    FeatureFlagService.client = Experiment.initializeWithAmplitudeAnalytics(deploymentKey);

    try {
      await FeatureFlagService.client.fetch();
    } catch (error) {
      console.error("Failed to fetch feature flag variants:", error);
    }

    FeatureFlagService.isInitialized = true;
  }

  /**
   * Look up the variant for a feature flag.
   * Returns an empty variant when the client is not initialized.
   */
  static getVariant(flagKey: FeatureFlagKeys): Variant {
    if (!FeatureFlagService.client) {
      return {};
    }
    return FeatureFlagService.client.variant(flagKey);
  }

  /**
   * Convenience: returns `true` when the variant value is one of: `on`, `true`, or `enabled`.
   *
   * Returns `false` otherwise (including when the client has not initialized yet or the key is unknown).
   */
  static isEnabled(flagKey: FeatureFlagKeys): boolean {
    const value = FeatureFlagService.getVariant(flagKey).value;
    if (value === undefined || value === null) return false;

    const normalized = value.toLowerCase();
    return normalized === "on" || normalized === "true" || normalized === "enabled";
  }

  /**
   * Returns all variants for the user, or an empty object when not initialized.
   */
  static getAllVariants(): Variants {
    if (!FeatureFlagService.client) {
      return {};
    }
    return FeatureFlagService.client.all();
  }

  static getIsInitialized() {
    return FeatureFlagService.isInitialized;
  }

  /**
   * Clear cached variants and reset initialization state.
   */
  static clear() {
    if (FeatureFlagService.client) {
      FeatureFlagService.client.clear();
      FeatureFlagService.client = null;
    }
    FeatureFlagService.isInitialized = false;
  }
}

export default FeatureFlagService;
